import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getFirestore, doc, getDoc, collection, addDoc } from 'firebase/firestore';
import { toast } from 'react-hot-toast';
import { motion } from 'framer-motion';

const SurveyResponse = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [responses, setResponses] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [clientInfo, setClientInfo] = useState({
    name: '',
    email: ''
  });

  const firestore = getFirestore();

  useEffect(() => {
    const fetchSurvey = async () => {
      try {
        const surveyRef = doc(firestore, 'surveys', surveyId);
        const surveySnap = await getDoc(surveyRef);

        if (surveySnap.exists()) {
          const surveyData = surveySnap.data();
          setSurvey({ id: surveySnap.id, ...surveyData });
          
          // Initialize responses object
          const initialResponses = {};
          surveyData.questions.forEach(question => {
            initialResponses[question.id] = '';
          });
          setResponses(initialResponses);
        } else {
          setError('Survey not found or has been removed');
        }
      } catch (err) {
        console.error('Error fetching survey:', err);
        setError('Failed to load survey');
      } finally {
        setIsLoading(false);
      }
    };

    if (surveyId) {
      fetchSurvey();
    }
  }, [surveyId, firestore]);

  const handleResponseChange = (questionId, value) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const validateResponses = () => {
    const requiredQuestions = survey.questions.filter(q => q.required);
    const missingResponses = requiredQuestions.filter(q => !responses[q.id] || responses[q.id].toString().trim() === '');
    
    if (missingResponses.length > 0) {
      toast.error(`Please answer all required questions`);
      return false;
    }

    if (!survey.allowAnonymous && (!clientInfo.name.trim() || !clientInfo.email.trim())) {
      toast.error('Please provide your name and email');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateResponses()) return;

    setIsSubmitting(true);
    try {
      const responseData = {
        surveyId,
        responses,
        clientInfo: survey.allowAnonymous ? null : clientInfo,
        submittedAt: new Date().toISOString(),
        isAnonymous: survey.allowAnonymous
      };

      await addDoc(collection(firestore, 'surveyResponses'), responseData);
      
      toast.success('Thank you! Your feedback has been submitted.');
      
      // Redirect to a thank you page or back to home after 2 seconds
      setTimeout(() => {
        navigate('/survey-thank-you');
      }, 2000);
      
    } catch (error) {
      console.error('Error submitting response:', error);
      toast.error('Failed to submit response. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderQuestion = (question, index) => {
    const value = responses[question.id] || '';

    switch (question.type) {
      case 'text':
        return (
          <textarea
            value={value}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            placeholder="Type your response here..."
            rows={4}
            className="w-full px-4 py-3 bg-gray-50 dark:bg-[#1A1A1A] border border-gray-200 dark:border-[#333] rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-brand focus:border-transparent resize-none"
          />
        );

      case 'rating':
        return (
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((rating) => (
              <button
                key={rating}
                onClick={() => handleResponseChange(question.id, rating)}
                className={`w-12 h-12 rounded-full border-2 transition-all duration-200 flex items-center justify-center font-medium ${
                  value === rating
                    ? 'bg-brand border-brand text-black'
                    : 'border-gray-200 dark:border-[#333] text-gray-600 dark:text-gray-400 hover:border-brand hover:text-brand'
                }`}
              >
                {rating}
              </button>
            ))}
            <div className="ml-4 text-sm text-gray-600 dark:text-gray-400">
              <span className="block">1 = Poor</span>
              <span className="block">5 = Excellent</span>
            </div>
          </div>
        );

      case 'yes_no':
        return (
          <div className="flex gap-4">
            {['Yes', 'No'].map((option) => (
              <button
                key={option}
                onClick={() => handleResponseChange(question.id, option)}
                className={`px-6 py-3 rounded-lg border-2 transition-all duration-200 font-medium ${
                  value === option
                    ? 'bg-brand border-brand text-black'
                    : 'border-gray-200 dark:border-[#333] text-gray-600 dark:text-gray-400 hover:border-brand hover:text-brand'
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        );

      case 'multiple_choice':
        return (
          <div className="space-y-2">
            {question.options?.map((option, optionIndex) => (
              <button
                key={optionIndex}
                onClick={() => handleResponseChange(question.id, option)}
                className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                  value === option
                    ? 'bg-brand/10 border-brand text-brand'
                    : 'border-gray-200 dark:border-[#333] text-gray-600 dark:text-gray-400 hover:border-brand hover:bg-brand/5'
                }`}
              >
                <div className="flex items-center">
                  <div className={`w-4 h-4 rounded-full border-2 mr-3 ${
                    value === option ? 'border-brand bg-brand' : 'border-gray-300 dark:border-gray-600'
                  }`}>
                    {value === option && (
                      <div className="w-2 h-2 bg-black rounded-full mx-auto mt-0.5" />
                    )}
                  </div>
                  {option}
                </div>
              </button>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white dark:bg-black">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex flex-col items-center gap-4"
        >
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-brand" />
          <p className="text-sm text-gray-600 dark:text-[#666666]">Loading survey...</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white dark:bg-black p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-white dark:bg-black rounded-2xl p-6 text-center border border-gray-200 dark:border-[#1A1A1A] shadow-sm"
        >
          <div className="w-16 h-16 mx-auto mb-6 bg-red-50 dark:bg-red-900/20 rounded-2xl flex items-center justify-center">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 tracking-tight">Survey Error</h1>
          <p className="text-base text-gray-600 dark:text-[#666666] mb-6 leading-relaxed">
            {error}
          </p>
          <motion.button
            onClick={() => navigate('/')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full py-3 px-6 bg-brand hover:bg-brand-hover text-black font-medium rounded-xl transition-all duration-200"
          >
            Go to Home
          </motion.button>
        </motion.div>
      </div>
    );
  }

  if (!survey) return null;

  return (
    <div className="min-h-screen bg-white dark:bg-black">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Header */}
          <div className="text-center space-y-4">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white tracking-tight">
              {survey.title}
            </h1>
            {survey.description && (
              <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed max-w-2xl mx-auto">
                {survey.description}
              </p>
            )}
            <div className="text-sm text-gray-500 dark:text-gray-500">
              {survey.questions.length} question{survey.questions.length !== 1 ? 's' : ''}
              {survey.questions.filter(q => q.required).length > 0 && (
                <span> • {survey.questions.filter(q => q.required).length} required</span>
              )}
            </div>
          </div>

          {/* Client Info (if not anonymous) */}
          {!survey.allowAnonymous && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-gray-50 dark:bg-[#1A1A1A] rounded-xl p-6 space-y-4"
            >
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Your Information</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={clientInfo.name}
                    onChange={(e) => setClientInfo(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Your full name"
                    className="w-full px-4 py-3 bg-white dark:bg-[#252525] border border-gray-200 dark:border-[#333] rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-brand focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={clientInfo.email}
                    onChange={(e) => setClientInfo(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 bg-white dark:bg-[#252525] border border-gray-200 dark:border-[#333] rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-brand focus:border-transparent"
                  />
                </div>
              </div>
            </motion.div>
          )}

          {/* Questions */}
          <div className="space-y-8">
            {survey.questions.map((question, index) => (
              <motion.div
                key={question.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * (index + 1) }}
                className="bg-white dark:bg-[#111111] rounded-xl p-6 border border-gray-200 dark:border-[#1A1A1A] shadow-sm"
              >
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white leading-relaxed pr-4">
                      {question.text}
                      {question.required && <span className="text-red-500 ml-1">*</span>}
                    </h3>
                    <span className="text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-[#1A1A1A] px-2 py-1 rounded-full flex-shrink-0">
                      {index + 1} of {survey.questions.length}
                    </span>
                  </div>
                  
                  {renderQuestion(question, index)}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Submit Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 * (survey.questions.length + 1) }}
            className="text-center pt-8"
          >
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-8 py-4 bg-brand hover:bg-brand-hover text-black font-medium rounded-xl transition-all duration-300 text-lg shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-3 mx-auto"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                  Submitting...
                </>
              ) : (
                <>
                  Submit Feedback
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </>
              )}
            </button>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default SurveyResponse; 